import { Locale } from '@/common'
import Data from '@/data'
import { ItemFlags } from '@/data/enums'
import Trans from '@/data/trans'
import { Range } from '@/data/types'
import { itemFixSockets, itemGetMods, itemTranslationDomain, itemUpdateStats } from '@/logic/item'
import { checkCondition, forceCondition, modStats, modStatsMax } from '@/logic/mods'
import { uniqueModTypes } from '@/logic/uniqueMods'
import { StatType } from '@/types/item'
import { OptionOrGroup, SelectOption, underlinerFunc } from '@/ui/select'
import Tooltip from '@/ui/tooltip'
import { faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import React from 'react'
import { ItemProps } from './ItemEditor'
import { ModCategory } from './ItemMods'
import { TextLine } from './TextLine'
import { AbstractCategory, categoryNames } from './category'
import { SelectLine } from './select'

function UniqueStatLine({
  item,
  onChange,
  id,
  limits
}: {
  id: number
  limits: Range[]
} & ItemProps) {
  const domain = itemTranslationDomain(Data.items[item.base])
  const stat = Trans[domain][id]

  const onSelect = React.useCallback(
    (opt?: SelectOption<number>) =>
      opt &&
      onChange((item) => {
        item = { ...item }
        item.stats = { ...item.stats }
        const stats = (item.stats.unique = { ...item.stats.unique })
        const rule = stat[Locale][opt.id]
        stat.ids.forEach((id, index) => {
          stats[id] =
            stats[id] != null
              ? forceCondition(rule.condition[index], stats[id])
              : rule.condition[index].max ?? rule.condition[index].min ?? 1
        })
        return itemFixSockets(item)
      }),
    [stat, onChange]
  )

  const indices = React.useMemo(() => {
    const indices: number[] = []
    stat[Locale].forEach((rule, index) => {
      if (rule.condition.every((cond, index) => checkCondition(cond, limits[index], false))) {
        indices.push(index)
      }
    })
    const ignored = indices.map((idx) => stat[Locale][idx].format.filter((f) => f === 'ignore').length)
    const minIgnore = Math.min(...ignored)
    return indices.filter((_, i) => ignored[i] === minIgnore)
  }, [stat, limits])

  const onStatChange = React.useCallback(
    (stat: string, value: number) =>
      onChange((item) => {
        item = { ...item }
        item.stats = { ...item.stats }
        item.stats.unique = { ...item.stats.unique }
        item.stats.unique[stat] = value
        return itemFixSockets(item)
      }),
    [onChange]
  )

  const values = stat.ids.map((id) => item.stats.unique?.[id] ?? 0)
  const index =
    indices.find((si) => stat[Locale][si].condition.every((cond, index) => checkCondition(cond, values[index]))) ??
    indices[0]

  const getOptions = React.useCallback(
    () =>
      indices.map((index) => {
        const rule = stat[Locale][index]
        const stats: Record<string, Range> = {}
        stat.ids.forEach((id, index) => {
          stats[id] = forceCondition(rule.condition[index], limits[index])
        })
        const text = Trans[domain].formatStats(stats)
        return { id: index, name: text.join('\n') }
      }),
    [stat, indices, limits, domain]
  )

  const renderOption = React.useCallback(
    (opt: SelectOption<number>, search?: RegExp) => <span>{underlinerFunc(opt.name, search)}</span>,
    []
  )

  if (!indices.length) return null

  return (
    <li className="poe-StatLine">
      <SelectLine
        options={getOptions}
        renderOption={renderOption}
        id={index}
        onChange={onSelect}
        disabled={indices.length <= 1}>
        {(expanded) => (
          <>
            <TextLine
              domain={domain}
              id={Number(id)}
              stats={item.stats.unique ?? {}}
              index={index}
              limits={limits}
              onChange={onStatChange}
            />
            {!expanded && indices.length > 1 && (
              <div className="poe-mods-info">
                <FontAwesomeIcon icon={faChevronDown} className="poe-arrow" />
              </div>
            )}
          </>
        )}
      </SelectLine>
    </li>
  )
}

type ModOption = {
  id: string
  name: string
  disabled?: string
}

function UniqueModLine({
  type,
  item,
  onChange,
  id,
  list
}: {
  type: StatType
  id?: string
  list: string[]
} & ItemProps) {
  const onRemove = React.useCallback(
    () =>
      onChange((item) => {
        item = { ...item }
        item.mods = { ...item.mods! }
        item.mods[type] = { ...item.mods[type] }
        if (id) delete item.mods[type]![id]
        return itemUpdateStats(item, type)
      }),
    [id, type, onChange]
  )
  const onSelect = React.useCallback(
    (opt?: ModOption) =>
      opt &&
      onChange((item) => {
        item = { ...item }
        item.mods = { ...item.mods! }
        const entries = Object.entries(item.mods[type] ?? {})
        const index = entries.findIndex((e) => e[0] === id)
        entries[index >= 0 ? index : entries.length] = [opt.id, modStatsMax(Data.mods[opt.id])]
        item.mods[type] = Object.fromEntries(entries)
        return itemUpdateStats(item, type)
      }),
    [id, type, onChange]
  )
  const onStatChange = React.useCallback(
    (stat: string, value: number) =>
      onChange((item) => {
        if (!id) return item
        const stats = item.mods![type]?.[id]
        if (!stats || stats[stat] === value) return item

        const range = Data.mods[id].stats.find((s) => s.id === stat)
        if (!range) return item

        item = { ...item }
        item.mods = { ...item.mods! }
        const mods = (item.mods[type] = { ...item.mods[type] })
        mods[id] = { ...stats, [stat]: forceCondition(range, value) }
        return itemUpdateStats(item, type)
      }),
    [id, type, onChange]
  )

  const getOptions = React.useCallback((): OptionOrGroup<ModOption>[] => {
    const groups = new Set<string>()
    // Apparently they don't check for groups?
    // for (const mid of Object.keys(item.mods![type] ?? {})) {
    //   if (mid === id) continue
    //   const mod = Data.mods[mid]
    //   for (const g of mod.groups) groups.add(g)
    // }
    const domain = itemTranslationDomain(Data.items[item.base])
    function makeOption(mid: string) {
      const mod = Data.mods[mid]
      const option: ModOption = {
        id: mid,
        name: Trans[domain].formatStats(modStats(mod)).join('\n')
      }
      if (mid !== id && item.mods?.[type]?.[mid]) {
        option.disabled = 'This mod is already present on the item'
      } else if (mod.groups.some((g) => groups.has(g))) {
        option.disabled = 'Mod of this type already present on the item'
      }
      return option
    }

    const options = list.map(makeOption)
    if (type === 'implicit' && item.flags & ItemFlags.Corrupted) {
      const mods = itemGetMods(item, true, true)
      return [
        {
          title: 'Unique',
          options
        },
        {
          title: 'Corrupted',
          options: Object.keys(mods.implicit)
            .filter((id) => Data.mods[id].generation_type === 'corrupted')
            .map(makeOption)
        }
      ]
    }
    return options
  }, [list, item, type, id])

  const renderOption = React.useCallback(
    ({ name, disabled }: ModOption, search?: RegExp) => (
      <Tooltip tooltip={disabled} disabled={!disabled}>
        <span>{underlinerFunc(name, search)}</span>
      </Tooltip>
    ),
    []
  )

  return (
    <li className={classNames('poe-StatLine', { 'poe-empty': !id })}>
      {id != null && (
        <span className="poe-remove" title="Remove stat" onClick={onRemove}>
          <FontAwesomeIcon icon={faTimes} />
        </span>
      )}
      <SelectLine options={getOptions} renderOption={renderOption} id={id} onChange={onSelect}>
        {(expanded) => {
          const mod = id && Data.mods[id]
          if (!mod) return <div>{`<Add ${categoryNames[type]} Mod>`}</div>

          const domain = itemTranslationDomain(Data.items[item.base])
          const limits = modStats(mod)
          const stats = item.mods![type]?.[id] ?? {}
          const lines = Trans[domain].maskFromStatList(stats)
          const result = Object.keys(lines)
            .map(Number)
            .map((index) => {
              return (
                <TextLine
                  domain={domain}
                  key={index}
                  id={index}
                  stats={stats}
                  limits={Trans.all[index].ids.map((id) => limits[id] ?? { min: 0, max: 0 })}
                  onChange={onStatChange}
                  disabled={expanded}
                />
              )
            })
          if (!expanded) {
            result.push(
              <div className="poe-mods-info" key="info">
                <FontAwesomeIcon icon={faChevronDown} className="poe-arrow" />
              </div>
            )
          }
          return result
        }}
      </SelectLine>
    </li>
  )
}

export function UniqueModCategory({
  item,
  onChange,
  type,
  mods
}: {
  type: StatType
  mods: ReturnType<typeof itemGetMods>
} & ItemProps) {
  const randomMods = React.useMemo(() => {
    const onItem = item.mods?.[type] ?? {}
    if (type === 'implicit') {
      // We have corrupted implicits, use default mod picker
      if (Object.keys(onItem).some((id) => Data.mods[id].generation_type === 'corrupted')) return
    }
    const variant = Data.uniques[item.unique!].variants[item.variant!]
    if (!variant?.random_mods) return
    const random = variant.random_mods.filter((mod) => (mod.statType ?? 'explicit') === type)
    if (!random.length) return
    return random.map((mod) => {
      const list = mod.type === 'list' ? mod.list : uniqueModTypes[mod.type].list()
      return {
        list,
        count: mod.count,
        used: Object.keys(onItem).filter((id) => list.includes(id)).length
      }
    })
  }, [item, type])

  if (!randomMods?.length) {
    // Only default to regular mods for implicits and enchants
    if (type !== 'implicit' && type !== 'enchant') return null
    if (!mods[type] || !Object.keys(mods[type]!).length) return null
    return <ModCategory item={item} onChange={onChange} type={type} />
  }

  const incomplete = randomMods.filter((mod) => mod.used < mod.count)
  return (
    <AbstractCategory name={categoryNames[type]}>
      {Object.keys(item.mods?.[type] ?? {}).map((id) => {
        const groups = randomMods.filter((mod) => mod.used < mod.count || mod.list.includes(id))
        return (
          <UniqueModLine
            key={id}
            id={id}
            item={item}
            list={groups.flatMap((g) => g.list)}
            onChange={onChange}
            type={type}
          />
        )
      })}
      {incomplete.length > 0 && (
        <UniqueModLine item={item} list={incomplete.flatMap((g) => g.list)} onChange={onChange} type={type} />
      )}
    </AbstractCategory>
  )
}

export function UniqueCategory({ item, onChange }: ItemProps) {
  const limits = Data.uniques[item.unique!]?.variants[item.variant ?? 0]?.stats
  const domain = itemTranslationDomain(Data.items[item.base])
  const trans = React.useMemo(() => {
    if (!limits) return
    const trans = Trans[domain].maskFromStatList(limits)
    return Object.fromEntries(
      Object.entries(trans).map(([id, limits]) => [
        id,
        limits.map((lim) => (typeof lim === 'object' ? lim : { min: lim, max: lim }))
      ])
    )
  }, [limits, domain])
  if (!trans || !Object.keys(trans).length) return null
  return (
    <AbstractCategory name="Unique">
      {Object.entries(trans).map(([id, limits]) => (
        <UniqueStatLine key={id} item={item} onChange={onChange} id={Number(id)} limits={limits} />
      ))}
    </AbstractCategory>
  )
}

export function UniqueCustomCategory({ item, onChange }: ItemProps) {
  const onRemoveCustom = (index: number) =>
    onChange((item) => {
      item = { ...item }
      item.customText = item.customText?.slice() || []
      item.customText.splice(index, 1)
      return item
    })
  const onSetCustom = (index: number, value: string) =>
    onChange((item) => {
      item = { ...item }
      item.customText = item.customText?.slice() || []
      item.customText[index] = value
      return item
    })

  return (
    <AbstractCategory name="Custom Text">
      {!!item.customText &&
        item.customText.map((text, index) => (
          <li className="poe-StatLine" key={index}>
            <span className="poe-remove" title="Remove stat" onClick={() => onRemoveCustom(index)}>
              <FontAwesomeIcon icon={faTimes} />
            </span>
            <div className="poe-stat-contents poe-custom-text">
              <input
                type="text"
                value={text}
                onChange={(e) => onSetCustom(index, e.target.value)}
                placeholder="<Custom Stat>"
              />
            </div>
          </li>
        ))}
      <li className="poe-StatLine poe-empty">
        <div className="poe-stat-contents poe-expandable" onClick={() => onSetCustom(item.customText?.length || 0, '')}>
          <div>{'<Add Stat>'}</div>
        </div>
      </li>
    </AbstractCategory>
  )
}
